import dayjs from 'dayjs';
import { isNil } from 'lodash-es';
import { type AxiosResponse } from 'axios';
import { OVERVIEW_TYPE } from '@/database/utils/overview.define';
import type { DBType, MetricRequest } from '@/common/utils/types';
import { makeApiKey } from '@/common/utils/abortApi.utils';
import { DB_TYPE } from '@/common/define/common.define';
import type {
  DataMetricResponse,
  MetricResponseItem,
  MetricV7Request,
} from '@/api-client/data/model';
import type { OverviewTimePeriod, OverviewType, DbId } from './overview.types';
import type { InstanceRole } from './types';
import { DATABASE_MULTI_VIEW_TYPES, DATABASE_SINGLE_VIEW_TYPES } from './define';
import { getPgMetricCategory } from './chart/metricFetch.utils';
import { getAPIErrorStatusText } from '@/common/utils/commonUtils';
import { TimePeriodInfo, UTC_TIME_FORMAT } from '@/common/components/molecules/timeController';
import { parsePeriodParts } from '@/common/utils/core';
import { applyLastTime } from '@/dashboard/features/chartDataHandler/domain/metric/applyLastTime';

export const isDatabaseOverview = (viewName?: string) => {
  const overviewTypes = Object.values(OVERVIEW_TYPE) as string[];
  return !!viewName && overviewTypes.includes(viewName);
};

export const isLegacyRtm = ({
  dbType,
  viewType,
}: {
  dbType?: string | null;
  viewType?: string;
}) => {
  if (!dbType || !viewType) {
    return false;
  }

  switch (viewType) {
    case OVERVIEW_TYPE.SINGLE_VIEW:
      return Object.keys(DATABASE_SINGLE_VIEW_TYPES).includes(dbType);
    case OVERVIEW_TYPE.MULTI_VIEW:
      return Object.keys(DATABASE_MULTI_VIEW_TYPES).includes(dbType);
    default:
      return false;
  }
};

export const getTimeRangeFromPeriod = (
  period: NonNullable<MetricRequest['period']>,
  requestTime?: string,
): OverviewTimePeriod => {
  const [value, unit] = parsePeriodParts(period) ?? [];
  if (!value || !unit || !requestTime) {
    return {
      period,
    };
  }

  const reqTimeDayjs = dayjs(requestTime);

  return {
    fromTime: reqTimeDayjs.subtract(value, unit).format(UTC_TIME_FORMAT),
    toTime: reqTimeDayjs.format(UTC_TIME_FORMAT),
  };
};

export const getTimePeriodParams = (
  period: NonNullable<MetricRequest['period']>,
  info?: TimePeriodInfo,
): OverviewTimePeriod => {
  const { isPaused, toTimeUtc } = info ?? {};
  if (!info || !isPaused) {
    return {
      period,
    };
  }
  const [value, unit] = parsePeriodParts(period) ?? [];

  if (!value || !unit || !toTimeUtc) {
    return {
      period,
    };
  }

  return {
    fromTime: dayjs(toTimeUtc).subtract(value, unit).format(UTC_TIME_FORMAT),
    toTime: toTimeUtc,
  };
};

export const hasDbSelect = ({
  dbType,
  view,
}: {
  dbType?: DBType | null;
  view?: OverviewType | null;
}) => {
  return dbType === DB_TYPE.POSTGRESQL && view === OVERVIEW_TYPE.SINGLE_VIEW;
};

export const isValidDbType = (dbType?: string | null): dbType is NonNullable<DBType> => {
  return !isNil(dbType) && (Object.values(DB_TYPE) as string[]).includes(dbType);
};

export const isValidViewType = (viewType?: string | null): viewType is OverviewType => {
  return !isNil(viewType) && (Object.values(OVERVIEW_TYPE) as string[]).includes(viewType);
};

export const getOverviewAbortKey = ({
  viewType,
  dbType,
  fnName,
}: {
  viewType: OverviewType;
  dbType: NonNullable<DBType>;
  fnName: string;
}) => {
  return makeApiKey({ prefix: `${dbType}-${viewType}`, categoryId: fnName });
};

// metricName 중복 제거, interval은 의미없는 값으로 추정됨
const removeDuplicateMetricInfo = (metricNames: string[]): string[] => {
  return [...new Set(metricNames)];
};

export const getDbMetricInfoRequestsMySQL = ({
  metricNames,
  instanceIds,
  globalTimeInfo,
  lastTime,
}: {
  metricNames: string[];
  instanceIds: string[];
  globalTimeInfo?: TimePeriodInfo;
  lastTime?: string;
}): MetricV7Request[] => {
  const deduplicationMetricInfo = removeDuplicateMetricInfo(metricNames);
  const timeParams = getTimePeriodParams('p10m', globalTimeInfo);

  return deduplicationMetricInfo.map<MetricRequest>((curr) => {
    const baseRequest: MetricRequest = {
      aggregationType: 'byTarget',
      interval: 'I5s',
      category: DB_TYPE.MYSQL,
      dataId: curr,
      summaryType: 'current',
      targetIds: instanceIds,
      ...timeParams,
    };

    if (lastTime) {
      return applyLastTime(
        {
          ...baseRequest,
          skipIntervalFilling: true,
        },
        lastTime,
      );
    }

    return {
      ...baseRequest,
      interpolateType: 'Linear',
    };
  });
};

export const getDbMetricInfoRequestsPostgreSQL = ({
  metricNames,
  instanceIds,
  dbIds,
  instanceRole,
  globalTimeInfo,
  lastTime,
}: {
  metricNames: string[];
  instanceIds: string[];
  dbIds?: DbId[];
  instanceRole?: InstanceRole;
  globalTimeInfo?: TimePeriodInfo;
  lastTime?: string;
}): MetricV7Request[] => {
  const deduplicationMetricInfo = removeDuplicateMetricInfo(metricNames);

  const targetIds = !isNil(dbIds) ? dbIds : instanceIds;
  const category = getPgMetricCategory({ instanceRole });
  const timeParams = getTimePeriodParams('p10m', globalTimeInfo);

  return deduplicationMetricInfo.map<MetricRequest>((curr) => {
    const baseRequest: MetricRequest = {
      aggregationType: 'byTarget',
      interval: 'I5s',
      category,
      dataId: curr,
      summaryType: 'current',
      targetIds,
      ...timeParams,
    };

    if (lastTime) {
      return applyLastTime(
        {
          ...baseRequest,
          skipIntervalFilling: true,
        },
        lastTime,
      );
    }

    return {
      ...baseRequest,
      interpolateType: 'Linear',
    };
  });
};

export const getMetricResultStatusText = (
  result: PromiseSettledResult<AxiosResponse<DataMetricResponse, any, {}>>,
) => {
  if (result.status === 'fulfilled') {
    const { status, reason } = result.value.data?.data?.[0] || {};
    if (status === 'success') {
      return '';
    }
    return reason ?? '';
  }
  return getAPIErrorStatusText(result.reason);
};

export const sortMetricResponseByAlphabetical = (response: MetricResponseItem[]) => {
  return response.sort((a, b) => a.targetId?.localeCompare(b.targetId ?? '') ?? 0);
};
